// Position théorique (stock comptable) d'un périmètre (réservoir, ou position d'un propriétaire dans un
// réservoir) à partir de ses événements (cadrage 3.1, page 41 du dossier) :
//   stock initial + entrées validées - sorties validées +/- ajustements autorisés.
// Les événements non validés (brouillon, déclaré, en attente, contesté, annulé, archivé) sont ignorés et
// renvoyés avec leur motif. Un transfert est une sortie et une entrée liées ; une cession déplace une
// position d'un propriétaire à un autre sans variation du stock du réservoir (les deux jambes s'annulent).

import { moins, oppose, plus, q } from './decimal-outils';

export const TYPES_MOUVEMENTS = [
  'entree',
  'sortie',
  'transfert',
  'cession',
  'ajustement',
  'retour',
  'remplissage_gpl',
] as const;
export type TypeMouvement = (typeof TYPES_MOUVEMENTS)[number];

export const SOUS_TYPES_AJUSTEMENT = [
  'correction_mesure',
  'perte_documentee',
  'reclassification',
  'regularisation_inventaire',
] as const;
export type SousTypeAjustement = (typeof SOUS_TYPES_AJUSTEMENT)[number];

export const LIBELLES_TYPES_MOUVEMENTS: Record<TypeMouvement, string> = {
  entree: 'Entrée',
  sortie: 'Sortie',
  transfert: 'Transfert',
  cession: 'Cession entre propriétaires',
  ajustement: 'Ajustement',
  retour: 'Retour en dépôt',
  remplissage_gpl: 'Remplissage GPL',
};

export const LIBELLES_SOUS_TYPES_AJUSTEMENT: Record<SousTypeAjustement, string> = {
  correction_mesure: 'Correction de mesure',
  perte_documentee: 'Perte documentée',
  reclassification: 'Reclassification',
  regularisation_inventaire: 'Régularisation d’inventaire',
};

/** Sens d'un mouvement pour le périmètre considéré. */
export type SensMouvement = 'entree' | 'sortie';

/** Sens imposé par le type ; null si le sens est porté par le mouvement (transfert, cession, ajustement, GPL). */
export function sensParType(type: TypeMouvement): SensMouvement | null {
  if (type === 'entree' || type === 'retour') return 'entree';
  if (type === 'sortie') return 'sortie';
  return null;
}

export interface EvenementMatiere {
  id: string;
  type: TypeMouvement | 'stock_initial';
  /** Quantité positive (chaîne décimale). */
  quantite: string;
  /** Sens pour le périmètre considéré ; obligatoire pour transfert, cession, ajustement et remplissage. */
  sens?: SensMouvement | null;
  /** État de validation du mouvement (page 41). */
  etat: string;
  horodatage?: string | Date;
}

export interface EvenementRetenu extends EvenementMatiere {
  effet: string;
}

export interface ResultatPosition {
  position: string;
  retenus: EvenementRetenu[];
  ignores: { evenement: EvenementMatiere; motif: string }[];
}

/** États qui font entrer un mouvement dans le stock comptable. */
export const ETATS_COMPTABILISES: readonly string[] = ['valide'];

export function positionTheorique(
  evenements: readonly EvenementMatiere[],
  options: { stockInitial?: string; etatsComptabilises?: readonly string[] } = {},
): ResultatPosition {
  const etats = options.etatsComptabilises ?? ETATS_COMPTABILISES;
  let position = options.stockInitial ?? '0';
  q(position);
  const retenus: EvenementRetenu[] = [];
  const ignores: ResultatPosition['ignores'] = [];

  const ordonnes = [...evenements].sort((a, b) => {
    const ta = a.horodatage ? new Date(a.horodatage).getTime() : 0;
    const tb = b.horodatage ? new Date(b.horodatage).getTime() : 0;
    return ta - tb || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  });

  for (const e of ordonnes) {
    if (!etats.includes(e.etat)) {
      ignores.push({ evenement: e, motif: `Événement non validé (état « ${e.etat} »)` });
      continue;
    }
    const quantite = q(e.quantite);
    if (quantite.m < 0n) {
      ignores.push({ evenement: e, motif: 'Quantité négative : le sens est porté par le champ « sens »' });
      continue;
    }
    const sens = e.type === 'stock_initial' ? 'entree' : (sensParType(e.type) ?? e.sens ?? null);
    if (!sens) {
      ignores.push({ evenement: e, motif: `Sens absent pour un mouvement de type « ${e.type} »` });
      continue;
    }
    if (e.type !== 'stock_initial' && sensParType(e.type) && e.sens && e.sens !== sensParType(e.type)) {
      ignores.push({ evenement: e, motif: `Sens « ${e.sens} » incompatible avec le type « ${e.type} »` });
      continue;
    }
    position = sens === 'entree' ? plus(position, e.quantite) : moins(position, e.quantite);
    retenus.push({ ...e, sens, effet: sens === 'entree' ? e.quantite : oppose(e.quantite) });
  }
  return { position, retenus, ignores };
}

export interface PositionProprietaire {
  proprietaireId: string;
  quantite: string;
}

/**
 * Cession entre propriétaires dans un même réservoir (cadrage 3.4) : déplace une position comptable sans
 * entrée ni sortie physique. Le total du réservoir est inchangé (R-04). Refusée si la position du cédant
 * est insuffisante, si les deux parties sont identiques ou si la quantité n'est pas strictement positive.
 */
export function appliquerCession(
  positions: readonly PositionProprietaire[],
  cedantId: string,
  cessionnaireId: string,
  quantite: string,
): { ok: true; positions: PositionProprietaire[] } | { ok: false; motif: string } {
  if (cedantId === cessionnaireId) return { ok: false, motif: 'Le cédant et le cessionnaire doivent être distincts' };
  if (q(quantite).m <= 0n) return { ok: false, motif: 'La quantité cédée doit être strictement positive' };
  const cedant = positions.find((p) => p.proprietaireId === cedantId);
  if (!cedant) return { ok: false, motif: 'Le cédant ne détient aucune position dans ce réservoir' };
  if (q(moins(cedant.quantite, quantite)).m < 0n) {
    return { ok: false, motif: 'Position du cédant insuffisante pour la quantité cédée' };
  }
  const resultat = positions.map((p) => ({ ...p }));
  for (const p of resultat) if (p.proprietaireId === cedantId) p.quantite = moins(p.quantite, quantite);
  const cible = resultat.find((p) => p.proprietaireId === cessionnaireId);
  if (cible) cible.quantite = plus(cible.quantite, quantite);
  else resultat.push({ proprietaireId: cessionnaireId, quantite });
  return { ok: true, positions: resultat };
}
